import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type PointerEvent as ReactPointerEvent } from 'react'
import type { Campaign, RegisteredAccount, Role, Task, UserProfile } from '../types'
import { buildDashboardSnapshot, periodOptions, reportingStartDate, type PeriodPreset } from '../reporting/dashboardData'
import { buildDashboardNarration, requestDashboardSpeech } from '../reporting/dashboardNarration'
import { taskMatchesOperation } from '../reporting/operationScope'
import { alterationMetricsForRange } from '../reporting/statusChanges'
import { buildCollaboratorPalantir, canViewCollaboratorPalantir, collaboratorEvaluationLabel, collaboratorEvaluationOptions, collaboratorEvaluationScore, collaboratorStarScore, type CollaboratorEvaluation, type CollaboratorFeedback } from '../reporting/collaboratorPalantir'
import { individualPeriodOptions, individualPeriodRange, type IndividualPeriod } from '../reporting/individualPeriod'
import { scaledVolumeCrosshairPoint, type VolumeCrosshairPoint } from '../reporting/volumeCrosshair'
import { campaignStatusLabel } from '../campaignStatus'
import { Icon } from './Icon'
import { SearchableUserSelect } from './SearchableUserSelect'
import { UserAvatar } from './UserAvatar'
import { HubOperationalBI } from './HubOperationalBI'
import { loadDocument, saveDocument } from '../documentApi'

interface Props {
  campaigns: Campaign[]
  tasks: Task[]
  role: Role
  accounts: RegisteredAccount[]
  currentUserProfile: UserProfile
  currentUser: string
  currentUserName: string
}

const reportThemeStorageKey = 'startracker-report-theme-v1'
const firstReportMonth = reportingStartDate.slice(0, 7)
const emptyFocus: string[] = []
export const reportThemeOptions = [
  { value: 'red', label: 'Vermelho' },
  { value: 'blue', label: 'Azul' },
  { value: 'green', label: 'Verde' },
  { value: 'black', label: 'Preto' },
  { value: 'gray', label: 'Cinza' },
] as const
export type ReportTheme = typeof reportThemeOptions[number]['value']
const reportAreaPalettes: Record<ReportTheme, string[]> = {
  red: ['#e61b10', '#f15f6b', '#ef929a', '#7aa9df', '#6c9bd4', '#5c91cc', '#aeb5bf', '#efc56d', '#f7dea0'],
  blue: ['#1465ff', '#4c86ed', '#8ab4f8', '#284f91', '#5c78a8', '#9aabc7', '#aeb5bf', '#62a8c7', '#a9d5e8'],
  green: ['#1f9d63', '#50b986', '#92d7b4', '#35765a', '#6b9b82', '#a9c6b7', '#aeb5bf', '#82aa55', '#c2d9a7'],
  black: ['#171b22', '#3d434d', '#777e89', '#242f43', '#526078', '#919aaa', '#b7bbc2', '#665f56', '#aaa49d'],
  gray: ['#6b7280', '#8d95a3', '#b6bcc5', '#596478', '#7c8798', '#aab1bd', '#c5c9d0', '#8c8984', '#c4c0ba'],
}

interface PeriodChoice {
  value: string
  label: string
  anchor: string
}

function localDateKey(date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function utcDate(value: string) {
  return new Date(`${value}T12:00:00.000Z`)
}

function dateKey(date: Date) {
  return date.toISOString().slice(0, 10)
}

function weekStartKey(value: string) {
  const date = utcDate(value)
  date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7)
  return dateKey(date)
}

function weekLabel(start: Date, isCurrent: boolean) {
  const end = new Date(start)
  end.setUTCDate(start.getUTCDate() + (7 - start.getUTCDay()) % 7)
  const formatter = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })
  const label = `${formatter.format(start).replace('.', '')} – ${formatter.format(end).replace('.', '')}`
  return isCurrent ? `${label} — vigente` : label
}

function availablePeriodChoices(today: string) {
  const effectiveMonth = today.slice(0, 7) < firstReportMonth ? firstReportMonth : today.slice(0, 7)
  const [endYear, endMonth] = effectiveMonth.split('-').map(Number)
  const weeks: PeriodChoice[] = []
  const months: PeriodChoice[] = []
  const quarters: PeriodChoice[] = []
  const semesters: PeriodChoice[] = []
  const years: PeriodChoice[] = []

  const firstWeek = utcDate(reportingStartDate)
  const currentWeekKey = weekStartKey(today)
  const currentWeek = utcDate(currentWeekKey)
  const firstWeekKey = weekStartKey(reportingStartDate)
  weeks.push({ value: reportingStartDate, label: weekLabel(firstWeek, firstWeekKey === currentWeekKey), anchor: reportingStartDate })
  const nextFullWeek = utcDate(firstWeekKey)
  nextFullWeek.setUTCDate(nextFullWeek.getUTCDate() + 7)
  for (const week = nextFullWeek; week <= currentWeek; week.setUTCDate(week.getUTCDate() + 7)) {
    const value = dateKey(week)
    weeks.push({ value, label: weekLabel(week, value === currentWeekKey), anchor: value })
  }

  for (let year = 2026; year <= endYear; year += 1) {
    const lastMonth = year === endYear ? endMonth : 12
    const firstMonth = year === 2026 ? 8 : 1

    for (let month = firstMonth; month <= lastMonth; month += 1) {
      const value = `${year}-${String(month).padStart(2, '0')}`
      const label = capitalize(new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}-01T12:00:00.000Z`)))
      months.push({ value, label, anchor: `${value}-01` })
    }

    const lastQuarter = year === endYear ? Math.floor((endMonth - 1) / 3) + 1 : 4
    for (let quarter = year === 2026 ? 3 : 1; quarter <= lastQuarter; quarter += 1) {
      const value = `${year}-Q${quarter}`
      quarters.push({ value, label: `${quarter}º trimestre de ${year}`, anchor: `${year}-${String((quarter - 1) * 3 + 1).padStart(2, '0')}-01` })
    }

    const lastSemester = year === endYear ? (endMonth <= 6 ? 1 : 2) : 2
    for (let semester = year === 2026 ? 2 : 1; semester <= lastSemester; semester += 1) {
      const value = `${year}-S${semester}`
      semesters.push({ value, label: `${semester}º semestre de ${year}`, anchor: `${year}-${semester === 1 ? '01' : '07'}-01` })
    }

    years.push({ value: String(year), label: String(year), anchor: `${year}-01-01` })
  }

  return { weeks: weeks.reverse(), months, quarters, semesters, years }
}

export function normalizeReportTheme(value: string | null): ReportTheme {
  return reportThemeOptions.some(option => option.value === value) ? value as ReportTheme : 'red'
}

function readSavedReportTheme() {
  try {
    return normalizeReportTheme(localStorage.getItem(reportThemeStorageKey))
  } catch {
    return 'red' as ReportTheme
  }
}

function formatNumber(value: number) {
  return value.toLocaleString('pt-BR')
}

function formatDuration(seconds: number) {
  const hours = Math.floor(seconds / 3600).toString().padStart(2, '0')
  const minutes = Math.floor(seconds % 3600 / 60).toString().padStart(2, '0')
  const remainingSeconds = Math.floor(seconds % 60).toString().padStart(2, '0')
  return `${hours}:${minutes}:${remainingSeconds}`
}

function signedPercent(value?: number) {
  if (value === undefined) return '—'
  return `${value > 0 ? '+' : ''}${value}%`
}

function feedbackDate(value: string) {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    .format(new Date(value)).replace('.', '')
}

export function Reports({ campaigns, tasks, role, accounts, currentUserProfile, currentUser, currentUserName }: Props) {
  const [operation, setOperation] = useState('Hub Sudeste')
  const [preset, setPreset] = useState<PeriodPreset>('month')
  const [today, setToday] = useState(localDateKey)
  const [dayAnchor, setDayAnchor] = useState(localDateKey)
  const choices = useMemo(() => availablePeriodChoices(today), [today])
  const [selectedWeek, setSelectedWeek] = useState(() => weekStartKey(localDateKey()))
  const [selectedMonth, setSelectedMonth] = useState(() => localDateKey().slice(0, 7) < firstReportMonth ? firstReportMonth : localDateKey().slice(0, 7))
  const [selectedQuarter, setSelectedQuarter] = useState(() => {
    const date = new Date(`${localDateKey()}T12:00:00`)
    return date.getFullYear() < 2026 || (date.getFullYear() === 2026 && date.getMonth() < 6) ? '2026-Q3' : `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`
  })
  const [selectedSemester, setSelectedSemester] = useState(() => {
    const date = new Date(`${localDateKey()}T12:00:00`)
    return date.getFullYear() < 2026 || (date.getFullYear() === 2026 && date.getMonth() < 6) ? '2026-S2' : `${date.getFullYear()}-S${date.getMonth() < 6 ? 1 : 2}`
  })
  const [selectedYear, setSelectedYear] = useState(() => String(Math.max(2026, new Date().getFullYear())))
  const [generatingDashboard, setGeneratingDashboard] = useState(false)
  const [generatingSpreadsheet, setGeneratingSpreadsheet] = useState(false)
  const [reportOptionsOpen, setReportOptionsOpen] = useState(false)
  const [error, setError] = useState('')
  const [focusByPeriod, setFocusByPeriod] = useState<Record<string, string[]>>({})
  const [focusFormOpen, setFocusFormOpen] = useState(false)
  const [focusDraft, setFocusDraft] = useState('')
  const [narrationState, setNarrationState] = useState<'idle' | 'loading' | 'playing'>('idle')
  const [narrationNotice, setNarrationNotice] = useState('')
  const [palantirContributor, setPalantirContributor] = useState('')
  const [palantirPeriod, setPalantirPeriod] = useState<IndividualPeriod>('month')
  const [feedbackDraft, setFeedbackDraft] = useState('')
  const [feedbackEvaluation, setFeedbackEvaluation] = useState<Partial<CollaboratorEvaluation>>({})
  const [pendingFeedbackDeleteId, setPendingFeedbackDeleteId] = useState<string | null>(null)
  const [collaboratorFeedbacks, setCollaboratorFeedbacks] = useState<CollaboratorFeedback[]>([])
  const [reportTheme, setReportTheme] = useState<ReportTheme>(readSavedReportTheme)
  const [volumeCrosshair, setVolumeCrosshair] = useState<VolumeCrosshairPoint | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const audioUrlRef = useRef<string | null>(null)
  const speechRequestRef = useRef<AbortController | null>(null)
  const narrationRunRef = useRef(0)
  const reportDocumentVersionRef = useRef(0)
  const persistedReportDocumentRef = useRef('')
  const [reportDocumentReady, setReportDocumentReady] = useState(false)
  const selectedChoice = preset === 'week'
    ? choices.weeks.find(item => item.value === selectedWeek)
    : preset === 'month'
      ? choices.months.find(item => item.value === selectedMonth)
      : preset === 'quarter'
        ? choices.quarters.find(item => item.value === selectedQuarter)
        : preset === 'semester'
          ? choices.semesters.find(item => item.value === selectedSemester)
          : preset === 'year'
            ? choices.years.find(item => item.value === selectedYear)
            : undefined
  const anchor = preset === 'day' ? dayAnchor : preset === 'all' ? today : selectedChoice?.anchor ?? today
  const operations = useMemo(() => ['Hub Sudeste', 'Todas as operações', ...new Set(campaigns.map(campaign => campaign.operation).filter((item): item is string => Boolean(item)))], [campaigns])
  const canFilterByContributor = canViewCollaboratorPalantir(currentUserProfile)
  const selectedReportTheme = reportThemeOptions.find(option => option.value === reportTheme) ?? reportThemeOptions[0]
  const snapshot = useMemo(() => buildDashboardSnapshot(tasks, campaigns, preset, anchor, operation), [tasks, campaigns, preset, anchor, operation])
  const crosshairMonth = volumeCrosshair && volumeCrosshair.index >= 0 ? snapshot.monthly[volumeCrosshair.index] : undefined
  const palantirRange = useMemo(() => individualPeriodRange(palantirPeriod, today), [palantirPeriod, today])
  const palantirAccount = accounts.find(account => account.initials === palantirContributor)
  const palantir = useMemo(() => palantirContributor
    ? buildCollaboratorPalantir(tasks, campaigns, operation, palantirRange.start, palantirRange.end, palantirContributor)
    : undefined, [campaigns, operation, palantirContributor, palantirRange.end, palantirRange.start, tasks])
  const palantirFeedbacks = collaboratorFeedbacks.filter(item => item.contributor === palantirContributor)
  const starScore = collaboratorStarScore(collaboratorFeedbacks, palantirContributor)
  const completeFeedbackEvaluation = feedbackEvaluation.competence && feedbackEvaluation.productivity && feedbackEvaluation.intelligence
    ? feedbackEvaluation as CollaboratorEvaluation
    : undefined
  const draftEvaluationScore = completeFeedbackEvaluation ? collaboratorEvaluationScore(completeFeedbackEvaluation) : undefined
  const collaboratorOptions = useMemo(() => accounts.map(account => ({ value: account.initials, name: account.name, profile: account.profile, email: account.email, picture: account.picture })), [accounts])
  const baseFocusPeriodKey = preset === 'all' ? `${operation}::all` : `${operation}::${preset}::${snapshot.start}::${snapshot.end}`
  const focusPeriodKey = baseFocusPeriodKey
  const legacyAllFocusKey = `${operation}::all::2000-01-01::2100-12-31`
  const periodFocus = focusByPeriod[focusPeriodKey] ?? (preset === 'all' ? focusByPeriod[legacyAllFocusKey] : undefined) ?? emptyFocus
  const canEditFocus = role !== 'worker' || canFilterByContributor
  const matchingTasks = tasks.filter(task => taskMatchesOperation(task, campaigns, operation))
  const alterationRows = alterationMetricsForRange(matchingTasks, campaigns, snapshot.start, snapshot.end).filter(item => item.totalChanges > 0 || item.clientRejections > 0)
  const alterationTotals = alterationRows.reduce((totals, item) => ({
    task: totals.task + item.taskChanges,
    deliverable: totals.deliverable + item.deliverableChanges,
    internal: totals.internal + item.internalChanges,
    client: totals.client + item.clientChanges,
    rejections: totals.rejections + item.clientRejections,
  }), { task: 0, deliverable: 0, internal: 0, client: 0, rejections: 0 })
  const topAreas = snapshot.areas.slice(0, 3)
  const areaColors = reportAreaPalettes[reportTheme]
  const maxMonth = Math.max(...snapshot.monthly.map(item => item.total), 1)
  const maxArea = Math.max(...topAreas.map(item => item.total), 1)
  let gradientPosition = 0
  const donutGradient = snapshot.areas.map((area, index) => {
    const start = gradientPosition
    gradientPosition += area.percentage
    return `${areaColors[index]} ${start}% ${gradientPosition}%`
  }).join(', ')
  const narration = useMemo(() => buildDashboardNarration(snapshot, operation, periodFocus), [operation, periodFocus, snapshot])

  useEffect(() => {
    setFocusFormOpen(false)
    setFocusDraft('')
  }, [focusPeriodKey, role])

  useEffect(() => {
    if (!canFilterByContributor) {
      setPalantirContributor('')
    }
  }, [canFilterByContributor])

  useEffect(() => {
    setFeedbackDraft('')
    setFeedbackEvaluation({})
    setPendingFeedbackDeleteId(null)
  }, [palantirContributor])

  useEffect(() => {
    let active = true
    loadDocument<{ focusByPeriod?: Record<string, string[]>; collaboratorFeedbacks?: CollaboratorFeedback[] }>('report-collaboration').then(document => {
      if (!active) return
      const nextFocus = document.data.focusByPeriod && typeof document.data.focusByPeriod === 'object' ? document.data.focusByPeriod : {}
      const nextFeedbacks = Array.isArray(document.data.collaboratorFeedbacks) ? document.data.collaboratorFeedbacks : []
      reportDocumentVersionRef.current = document.version
      persistedReportDocumentRef.current = JSON.stringify({ focusByPeriod: nextFocus, collaboratorFeedbacks: nextFeedbacks })
      setFocusByPeriod(nextFocus)
      setCollaboratorFeedbacks(nextFeedbacks)
      setReportDocumentReady(true)
    }).catch(reason => setError(reason instanceof Error ? reason.message : 'Não foi possível carregar os dados compartilhados dos relatórios.'))
    return () => { active = false }
  }, [])

  useEffect(() => {
    if (!reportDocumentReady || !canFilterByContributor) return
    const data = { focusByPeriod, collaboratorFeedbacks }
    const serialized = JSON.stringify(data)
    if (serialized === persistedReportDocumentRef.current) return
    const timer = window.setTimeout(() => {
      saveDocument('report-collaboration', { version: reportDocumentVersionRef.current, data }).then(document => {
        reportDocumentVersionRef.current = document.version
        persistedReportDocumentRef.current = JSON.stringify(document.data)
      }).catch(reason => setError(reason instanceof Error ? reason.message : 'Não foi possível salvar os dados compartilhados dos relatórios.'))
    }, 400)
    return () => window.clearTimeout(timer)
  }, [canFilterByContributor, collaboratorFeedbacks, focusByPeriod, reportDocumentReady])

  useEffect(() => {
    const timer = window.setInterval(() => setToday(current => {
      const next = localDateKey()
      return next === current ? current : next
    }), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    stopNarration()
    return stopNarration
  }, [narration])

  function releaseGeneratedAudio() {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.onplay = null
      audioRef.current.onended = null
      audioRef.current.onerror = null
      audioRef.current.src = ''
      audioRef.current = null
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current)
      audioUrlRef.current = null
    }
  }

  function stopNarration() {
    narrationRunRef.current += 1
    speechRequestRef.current?.abort()
    speechRequestRef.current = null
    releaseGeneratedAudio()
    window.speechSynthesis?.cancel()
    setNarrationState('idle')
  }

  function playBrowserNarration(runId: number) {
    if (!('speechSynthesis' in window) || !('SpeechSynthesisUtterance' in window)) {
      throw new Error('Este navegador não oferece reprodução de voz.')
    }
    const utterance = new SpeechSynthesisUtterance(narration)
    utterance.lang = 'pt-BR'
    utterance.rate = .94
    utterance.pitch = 1
    const preferredVoice = window.speechSynthesis.getVoices().find(voice => voice.lang.toLowerCase() === 'pt-br')
    if (preferredVoice) utterance.voice = preferredVoice
    utterance.onend = () => {
      if (narrationRunRef.current !== runId) return
      setNarrationState('idle')
      setNarrationNotice('Leitura concluída.')
    }
    utterance.onerror = () => {
      if (narrationRunRef.current !== runId) return
      setNarrationState('idle')
      setError('Não foi possível reproduzir a leitura do dashboard neste navegador.')
      setNarrationNotice('Falha na reprodução da leitura.')
    }
    window.speechSynthesis.cancel()
    setNarrationState('playing')
    setNarrationNotice('Leitura em reprodução com a voz de acessibilidade do navegador.')
    window.speechSynthesis.speak(utterance)
  }

  async function toggleNarration() {
    if (narrationState !== 'idle') {
      stopNarration()
      setNarrationNotice('Leitura interrompida.')
      return
    }

    const runId = ++narrationRunRef.current
    const controller = new AbortController()
    speechRequestRef.current = controller
    setNarrationState('loading')
    setNarrationNotice('Preparando a leitura do dashboard.')
    setError('')
    try {
      const speech = await requestDashboardSpeech(narration, controller.signal)
      speechRequestRef.current = null
      const audioUrl = URL.createObjectURL(speech.audio)
      const audio = new Audio(audioUrl)
      audioRef.current = audio
      audioUrlRef.current = audioUrl
      audio.onplay = () => {
        if (narrationRunRef.current !== runId) return
        setNarrationState('playing')
        setNarrationNotice(`Leitura com voz neural em reprodução${speech.provider === 'google-cloud-neural2' ? ' pelo Google Cloud' : ''}.`)
      }
      audio.onended = () => {
        if (narrationRunRef.current !== runId) return
        releaseGeneratedAudio()
        setNarrationState('idle')
        setNarrationNotice('Leitura concluída.')
      }
      audio.onerror = () => {
        if (narrationRunRef.current !== runId) return
        releaseGeneratedAudio()
        setNarrationState('idle')
        setError('Não foi possível reproduzir o áudio gerado.')
        setNarrationNotice('Falha na reprodução da leitura.')
      }
      await audio.play()
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === 'AbortError') return
      if (narrationRunRef.current !== runId) return
      speechRequestRef.current = null
      releaseGeneratedAudio()
      try {
        playBrowserNarration(runId)
      } catch (fallbackError) {
        setNarrationState('idle')
        setError(fallbackError instanceof Error ? fallbackError.message : 'Não foi possível reproduzir a leitura do dashboard.')
        setNarrationNotice('Falha na reprodução da leitura.')
      }
    }
  }

  function persistFocus(nextItems: string[]) {
    setFocusByPeriod(current => ({ ...current, [focusPeriodKey]: nextItems }))
  }

  function addFocus(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canEditFocus) return
    const value = focusDraft.trim()
    if (!value) return
    persistFocus([...periodFocus, value])
    setFocusDraft('')
    setFocusFormOpen(false)
  }

  function removeFocus(index: number) {
    if (!canEditFocus) return
    persistFocus(periodFocus.filter((_, itemIndex) => itemIndex !== index))
  }

  function addCollaboratorFeedback(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const message = feedbackDraft.trim()
    if (!canFilterByContributor || !palantirContributor || !message || !completeFeedbackEvaluation) return
    const feedback: CollaboratorFeedback = {
      id: crypto.randomUUID(), contributor: palantirContributor, author: currentUser,
      authorName: currentUserName, message, evaluation: completeFeedbackEvaluation, createdOn: new Date().toISOString(),
    }
    setCollaboratorFeedbacks(current => {
      return [feedback, ...current].slice(0, 250)
    })
    setFeedbackDraft('')
    setFeedbackEvaluation({})
  }

  function deleteCollaboratorFeedback(id: string) {
    if (!canFilterByContributor) return
    setCollaboratorFeedbacks(current => {
      return current.filter(item => item.id !== id)
    })
    setPendingFeedbackDeleteId(null)
  }

  function changeReportTheme(theme: ReportTheme) {
    setReportTheme(theme)
    localStorage.setItem(reportThemeStorageKey, theme)
  }

  function selectPreset(nextPreset: PeriodPreset) {
    if (nextPreset === 'week') setSelectedWeek(weekStartKey(today))
    setPreset(nextPreset)
  }

  async function exportDashboard() {
    setGeneratingDashboard(true)
    setError('')
    try {
      const { generateOperationalDashboard } = await import('../reporting/generateOperationalDashboard')
      await generateOperationalDashboard({ snapshot: { ...snapshot, focus: periodFocus }, operation })
    } catch (reason) {
      console.error(reason)
      setError('Não foi possível exportar o dashboard. Tente novamente.')
    } finally {
      setGeneratingDashboard(false)
    }
  }

  async function exportSpreadsheet() {
    setGeneratingSpreadsheet(true)
    setReportOptionsOpen(false)
    setError('')
    try {
      const { generateOperationalSpreadsheet } = await import('../reporting/generateOperationalSpreadsheet')
      await generateOperationalSpreadsheet({ snapshot: { ...snapshot, focus: periodFocus }, operation })
    } catch (reason) {
      console.error(reason)
      setError('Não foi possível gerar a planilha do dashboard.')
    } finally {
      setGeneratingSpreadsheet(false)
    }
  }

  function trackVolumePointer(event: ReactPointerEvent<HTMLDivElement>) {
    const chart = event.currentTarget
    const bounds = chart.getBoundingClientRect()
    const point = scaledVolumeCrosshairPoint(
      event.clientX,
      event.clientY,
      bounds,
      chart,
      snapshot.monthly.length,
    )
    chart.style.setProperty('--crosshair-x', `${point.x}px`)
    chart.style.setProperty('--crosshair-y', `${point.y}px`)
    setVolumeCrosshair(current => current
      && current.index === point.index
      && current.alignLeft === point.alignLeft
      && current.placeBelow === point.placeBelow
      ? current
      : point)
  }

  return <div className={`report-page report-theme-${reportTheme}`}>
    <div className="page-head report-page-head">
      <div><h1>Dashboard Executivo</h1><p>Visão executiva do volume, alterações, áreas e tendências.</p></div>
      <div className="report-actions">
        <div className={`report-options ${reportOptionsOpen ? 'open' : ''}`} onMouseEnter={() => setReportOptionsOpen(true)} onMouseLeave={() => setReportOptionsOpen(false)} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setReportOptionsOpen(false) }}>
          <button type="button" className="report-options-trigger" aria-haspopup="menu" aria-expanded={reportOptionsOpen} aria-label="Opções do relatório" title="Options" onClick={() => setReportOptionsOpen(open => !open)}><Icon name="options"/></button>
          <div className="report-options-panel" role="menu" aria-label="Opções do relatório">
            <section className="report-option-section">
              <div className="report-option-title"><Icon name="report"/><div><b>Exportar</b><small>Baixe os dados ou a apresentação</small></div></div>
              <div className="report-export-actions"><button type="button" role="menuitem" disabled={generatingSpreadsheet || generatingDashboard} onClick={exportSpreadsheet}>Planilha <small>.xlsx</small></button><button type="button" role="menuitem" disabled={generatingSpreadsheet || generatingDashboard} onClick={() => { setReportOptionsOpen(false); void exportDashboard() }}>Apresentação <small>.pptx</small></button></div>
            </section>
            <section className="report-option-section">
              <div className="report-option-title"><i className={`theme-dot theme-dot-${reportTheme}`} aria-hidden="true"/><div><b>Esquema de cor</b><small>{selectedReportTheme.label} selecionado</small></div></div>
              <div className="report-color-options" role="radiogroup" aria-label="Cor do Dashboard Executivo">{reportThemeOptions.map(option => <button key={option.value} type="button" role="radio" aria-checked={reportTheme === option.value} className={`report-color-option ${reportTheme === option.value ? 'active' : ''}`} onClick={() => changeReportTheme(option.value)} aria-label={option.label} title={option.label}><i className={`theme-dot theme-dot-${option.value} ${reportTheme === option.value ? 'active' : ''}`} aria-hidden="true"/><span>{option.label}</span></button>)}</div>
            </section>
            <section className="report-option-section report-listen-section">
              <button type="button" className={`report-listen-option ${narrationState !== 'idle' ? 'active' : ''}`} aria-pressed={narrationState === 'playing'} aria-describedby="ai-voice-disclosure" onClick={() => { setReportOptionsOpen(false); void toggleNarration() }}><Icon name="speaker"/><span><b>Ouvir com IA</b><small>{narrationState === 'loading' ? 'Preparando a leitura…' : narrationState === 'playing' ? 'Clique para interromper' : 'Narrar o resumo executivo'}</small></span><em aria-hidden="true">{narrationState === 'playing' ? '■' : '▶'}</em></button>
              <small id="ai-voice-disclosure" className="ai-voice-disclosure">Voz neural via API, com fallback do navegador</small>
            </section>
          </div>
        </div>
      </div>
    </div>
    <span className="sr-only" role="status" aria-live="polite">{narrationNotice}</span>

    <div className="report-filter-panel card">
      <div className="report-period-tabs" aria-label="Período do relatório">{periodOptions.map(option => <button key={option.value} type="button" className={preset === option.value ? 'active' : ''} onClick={() => selectPreset(option.value)}>{option.label}</button>)}</div>
      {preset === 'day' && <label><span>DATA</span><input type="date" value={dayAnchor} onChange={event => setDayAnchor(event.target.value)}/></label>}
      {preset === 'week' && <label><span>SEMANA</span><select aria-label="Semana do relatório" value={selectedWeek} onChange={event => setSelectedWeek(event.target.value)}>{choices.weeks.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>}
      {preset === 'month' && <label><span>MÊS</span><select aria-label="Mês do relatório" value={selectedMonth} onChange={event => setSelectedMonth(event.target.value)}>{choices.months.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>}
      {preset === 'quarter' && <label><span>TRIMESTRE</span><select aria-label="Trimestre do relatório" value={selectedQuarter} onChange={event => setSelectedQuarter(event.target.value)}>{choices.quarters.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>}
      {preset === 'semester' && <label><span>SEMESTRE</span><select aria-label="Semestre do relatório" value={selectedSemester} onChange={event => setSelectedSemester(event.target.value)}>{choices.semesters.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>}
      {preset === 'year' && <label><span>ANO</span><select aria-label="Ano do relatório" value={selectedYear} onChange={event => setSelectedYear(event.target.value)}>{choices.years.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>}
      <label><span>OPERAÇÃO REGIONAL</span><select value={operation} onChange={event => setOperation(event.target.value)}>{operations.map(item => <option key={item}>{item}</option>)}</select></label>
    </div>
    {error && <div className="report-error" role="alert">{error}</div>}

    <HubOperationalBI campaigns={campaigns} tasks={tasks} start={snapshot.start} end={snapshot.end} label={snapshot.label}/>

    <section className="operational-dashboard" aria-label={`Dashboard de ${snapshot.label}`}>
      <div className="dashboard-kpis">
        <article className="dashboard-kpi primary-kpi"><span>VOLUME TOTAL</span><strong>{formatNumber(snapshot.total)}</strong><small>entregas</small><em>{snapshot.totalChange !== undefined ? <>{signedPercent(snapshot.totalChange)} <i>vs. período anterior</i></> : <>GERAL<i>histórico + registros atuais</i></>}</em></article>
        <article className="dashboard-kpi alteration-kpi"><span>ALTERAÇÕES</span><strong>{formatNumber(snapshot.alterations)}</strong><small>alterações</small><div><b>{snapshot.alterationRate}%</b><i>sobre o total</i></div>{snapshot.alterationConcentration !== undefined && <em>{snapshot.alterationConcentration}% concentradas nas áreas críticas</em>}</article>
        <article className="dashboard-kpi"><span>TIME DIGITAL + OUTRAS ÁREAS</span><strong>{formatNumber(snapshot.digitalTotal)}</strong><small>peças digitais</small><em>{formatNumber(snapshot.digitalInternal)} Digital + {formatNumber(snapshot.digitalOther)} de outras áreas</em>{snapshot.digitalChange !== undefined && <b className="kpi-change">{signedPercent(snapshot.digitalChange)}</b>}</article>
        <article className="dashboard-kpi"><span>ACUMULADO {anchor.slice(0, 4)}</span><strong>{formatNumber(snapshot.yearTotal)}</strong><small>entregas</small><em>{signedPercent(snapshot.yearChange)} vs. ano anterior · {formatNumber(snapshot.previousYearTotal)}</em></article>
      </div>

      <div className="dashboard-charts">
        <article className="dashboard-widget volume-widget">
          <h2><Icon name="report"/> Evolução do volume mensal{preset === 'all' && <small>referência histórica</small>}</h2>
          <div
            className="volume-chart"
            style={{'--month-count': snapshot.monthly.length, '--crosshair-x': '0px', '--crosshair-y': '0px'} as CSSProperties}
            onPointerEnter={trackVolumePointer}
            onPointerMove={trackVolumePointer}
            onPointerLeave={() => setVolumeCrosshair(null)}
            aria-label="Gráfico de evolução do volume mensal. Mova o cursor para consultar mês e volume."
          >
            {snapshot.monthly.map((item, index) => {
              const barHeight = `${Math.max(3, item.total / maxMonth * 100)}%`
              const className = ['volume-month', item.selected ? 'selected' : '', volumeCrosshair?.index === index ? 'crosshair-active' : ''].filter(Boolean).join(' ')
              return <div className={className} style={{'--bar-height': barHeight} as CSSProperties} key={item.label}><b>{formatNumber(item.total)}</b><i style={{height: barHeight}}/><span>{item.label}</span></div>
            })}
            {volumeCrosshair && crosshairMonth && <div className="volume-crosshair-layer" aria-hidden="true">
              <i className="volume-crosshair-line horizontal"/>
              <i className="volume-crosshair-line vertical"/>
              <i className="volume-crosshair-center"/>
              <output className={`volume-crosshair-tooltip ${volumeCrosshair.alignLeft ? 'align-left' : ''} ${volumeCrosshair.placeBelow ? 'place-below' : ''}`}>
                <span>{crosshairMonth.label}</span>
                <strong>{formatNumber(crosshairMonth.total)} <small>entregas</small></strong>
              </output>
            </div>}
          </div>
        </article>
        <article className="dashboard-widget area-widget"><h2>Distribuição do volume por área</h2><div className="area-distribution"><div className="area-donut" style={{background: `conic-gradient(${donutGradient || '#eceff3 0 100%'})`}}><div><strong>{formatNumber(snapshot.areaTotal)}</strong><span>{snapshot.areaTotal === snapshot.total ? 'entregas' : 'classificadas'}</span></div></div><div className="area-legend">{snapshot.areas.map((area, index) => <div key={area.area}><i style={{background: areaColors[index]}}/><span>{area.area}</span><b>{formatNumber(area.total)}</b><em>{area.percentage}%</em></div>)}</div></div></article>
        <article className="dashboard-widget top-widget"><h2>Top 3 áreas em volume</h2><div className="top-area-bars">{topAreas.map((area, index) => <div key={area.area}><b>{formatNumber(area.total)}</b><i style={{height: `${Math.max(8, area.total / maxArea * 100)}%`, background: areaColors[index]}}/><span>{area.area}</span><em>{area.percentage}%</em></div>)}</div></article>
      </div>

      <div className="dashboard-section-title"><span>★</span><h2>Destaques — {snapshot.label}</h2></div>
      <div className="area-highlights">{snapshot.areas.map((area, index) => <article key={area.area} style={{'--area-color': areaColors[index]} as CSSProperties}><h3>{area.area}</h3><strong>{formatNumber(area.total)}</strong><span>entregas</span>{area.alterations !== undefined && <p><b>{formatNumber(area.alterations)}</b> ajustes</p>}{area.alterationRate !== undefined && <em>{area.alterationRate}% de alterações</em>}{area.change !== undefined && <em>{signedPercent(area.change)} vs. período anterior</em>}{area.note && <small>{area.note}</small>}</article>)}</div>

      <div className="dashboard-insights"><article><span>INSIGHT DO PERÍODO</span><p>{snapshot.insight}</p></article><article className="focus-card"><div className="focus-card-head"><span>FOCO PARA OS PRÓXIMOS PERÍODOS</span>{canEditFocus && !focusFormOpen && <button type="button" onClick={() => setFocusFormOpen(true)}><Icon name="plus"/> Adicionar</button>}</div><div className="focus-card-content">{periodFocus.length ? <ul>{periodFocus.map((item, index) => <li key={`${item}-${index}`}><span>{item}</span>{canEditFocus && <button type="button" aria-label={`Excluir foco: ${item}`} title="Excluir" onClick={() => removeFocus(index)}>×</button>}</li>)}</ul> : !focusFormOpen && <p className="focus-empty">Nenhum foco cadastrado para este período.</p>}{focusFormOpen && <form className="focus-form" onSubmit={addFocus}><textarea autoFocus rows={2} maxLength={180} value={focusDraft} onChange={event => setFocusDraft(event.target.value)} placeholder="Digite um foco curto para o período"/><div><small>{focusDraft.length}/180</small><button type="button" onClick={() => { setFocusFormOpen(false); setFocusDraft('') }}>Cancelar</button><button type="submit" disabled={!focusDraft.trim()}>Salvar</button></div></form>}</div></article></div>
    </section>

    <section className="card alteration-report-card">
      <div className="section-head"><div><h3>Alterações por campanha e tarefa</h3><p className="section-description">Separação auditável entre ajustes internos e refações posteriores a uma rejeição do cliente.</p></div><span className="alteration-period">{snapshot.label}</span></div>
      <div className="alteration-summary"><div><strong>{alterationTotals.internal + alterationTotals.client}</strong><span>alterações no período</span></div><div><strong>{alterationTotals.internal}</strong><span>alterações internas</span></div><div className="client-change-metric"><strong>{alterationTotals.client}</strong><span>após rejeição do cliente</span></div><div className="client-rejection-metric"><strong>{alterationTotals.rejections}</strong><span>rejeições do cliente</span></div><div><strong>{new Set(alterationRows.map(item => item.campaignId)).size}</strong><span>campanhas impactadas</span></div></div>
      {alterationRows.length ? <div className="alteration-table-wrap"><div className="alteration-table alteration-table-head"><span>Campanha</span><span>Tarefa / frente</span><span>Status atual</span><span>Interna<br/>campanha</span><span>Interna<br/>entregas</span><span>Rejeições<br/>cliente</span><span>Pós-rejeição<br/>campanha</span><span>Pós-rejeição<br/>entregas</span><span>Total<br/>tarefa</span><span>Total<br/>campanha</span></div>{alterationRows.map(item => <div className="alteration-table" key={item.taskId}><span>{item.campaign}</span><strong>{item.task}</strong><span className={`demand-status status-${item.taskStatus.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replaceAll(' ', '-')}`}>{campaignStatusLabel(item.taskStatus)}</span><b>{item.internalTaskChanges}</b><b>{item.internalDeliverableChanges}</b><b className="client-rejection-value">{item.clientRejections}</b><b className="client-change-value">{item.clientTaskChanges}</b><b className="client-change-value">{item.clientDeliverableChanges}</b><b className="alteration-total">{item.totalChanges}</b><b>{item.campaignTotalChanges}</b></div>)}</div> : <div className="empty-alterations"><Icon name="check"/><div><b>Sem alterações ou rejeições do cliente neste recorte</b><span>{snapshot.source !== 'live' ? 'O indicador histórico consolidado permanece disponível acima; a nova separação começa nos registros auditáveis do StarTracker.' : 'As próximas entradas em “Em alteração” e “Reprovada pelo cliente” serão classificadas automaticamente.'}</span></div></div>}
    </section>

    {canFilterByContributor && <section className="card collaborator-palantir" aria-label="Tracking individual por colaborador">
      <div className="collaborator-palantir-head">
        <div><span>VISÃO INDIVIDUAL</span><h2>Tracking individual</h2><p>Entregas, volumetria e sinais operacionais do colaborador no recorte selecionado.</p></div>
        <label className="palantir-period-filter"><span>PERÍODO INDIVIDUAL</span><select value={palantirPeriod} onChange={event => setPalantirPeriod(event.target.value as IndividualPeriod)} aria-label="Período da visão individual">{individualPeriodOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
        <div className="palantir-user-filter"><label htmlFor="palantir-user-select">PESQUISAR COLABORADOR</label><div id="palantir-user-select"><SearchableUserSelect value={palantirContributor} options={collaboratorOptions} onChange={setPalantirContributor} ariaLabel="Pesquisar ou selecionar colaborador" placeholder="Digite ou selecione um colaborador" searchPlaceholder="Digite nome, perfil ou e-mail"/></div></div>
      </div>

      {!palantir || !palantirAccount ? <div className="palantir-empty"><Icon name="search"/><div><b>Selecione uma pessoa para abrir a visão individual</b><span>Use a busca acima ou escolha um nome na lista de colaboradores cadastrados.</span></div></div> : <>
        <div className="palantir-person">
          <UserAvatar initials={palantirAccount.initials} name={palantirAccount.name} picture={palantirAccount.picture} className="palantir-avatar"/>
          <div className="palantir-person-copy"><h3>{palantirAccount.name}</h3><p>{palantirAccount.profile} · {palantirAccount.email}</p><div className="palantir-person-meta"><div className="palantir-period"><span>RECORTE ANALISADO</span><strong>{palantirRange.label}<i aria-hidden="true"/> {operation}</strong></div><div className="palantir-score"><span>Star Score</span><strong>{starScore === undefined ? '—' : starScore.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}<small>/10</small></strong></div></div></div>
        </div>
        <div className="palantir-kpis">
          <article><span>VOLUMETRIA ENTREGUE</span><strong>{formatNumber(palantir.completedVolume)}</strong><small>{palantir.completed} {palantir.completed === 1 ? 'entrega concluída' : 'entregas concluídas'}</small></article>
          <article><span>ENTREGAS COM ATUAÇÃO</span><strong>{formatNumber(palantir.deliverables)}</strong><small>em {palantir.campaigns} {palantir.campaigns === 1 ? 'campanha' : 'campanhas'}</small></article>
          <article><span>TAXA DE CONCLUSÃO</span><strong>{palantir.completionRate}%</strong><small>{palantir.active} {palantir.active === 1 ? 'entrega ativa' : 'entregas ativas'} agora</small></article>
          <article><span>TEMPO REGISTRADO</span><strong>{formatDuration(palantir.seconds)}</strong><small>no período selecionado</small></article>
          <article><span>TAREFAS EM EXECUÇÃO</span><strong>{formatNumber(palantir.executingTasks)}</strong><small>sob responsabilidade direta agora</small></article>
        </div>
        <div className="palantir-body">
          <div className="palantir-analysis">
            <article className="palantir-panel palantir-behavior"><div className="palantir-panel-title"><div><span>COMPORTAMENTO OPERACIONAL</span><h3>Sinais do período</h3></div><small>Dados observáveis, sem score subjetivo</small></div><div className="behavior-signals"><div><b>{palantir.onTimeRate === undefined ? '—' : `${palantir.onTimeRate}%`}</b><span>entregas no prazo</span></div><div><b>{palantir.collaborations}</b><span>atuações como apoio</span></div><div><b>{palantir.alterations}</b><span>ciclos de alteração vinculados</span></div><div><b>{palantir.active}</b><span>entregas em andamento</span></div></div></article>
            <article className="palantir-panel"><div className="palantir-panel-title"><div><span>VOLUMETRIA</span><h3>Distribuição por área</h3></div></div>{palantir.areas.length ? <div className="palantir-area-bars">{palantir.areas.map(area => <div key={area.area}><span><b>{area.area}</b><em>{area.volume} · {area.percentage}%</em></span><i><b style={{ width: `${area.percentage}%` }}/></i></div>)}</div> : <p className="palantir-panel-empty">Nenhuma entrega concluída com área classificada neste recorte.</p>}</article>
            <article className="palantir-panel"><div className="palantir-panel-title"><div><span>FLUXO ATUAL</span><h3>Entregas por etapa</h3></div></div>{palantir.statuses.length ? <div className="palantir-statuses">{palantir.statuses.map(item => <span key={item.status} className={`status-${item.status.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replaceAll(' ', '-')}`}><b>{item.total}</b>{item.status}</span>)}</div> : <p className="palantir-panel-empty">Sem atividade registrada no período.</p>}</article>
            <article className="palantir-panel palantir-recent"><div className="palantir-panel-title"><div><span>HISTÓRICO RECENTE</span><h3>Últimas entregas concluídas</h3></div></div>{palantir.recentDeliveries.length ? <div>{palantir.recentDeliveries.map(item => <div key={item.id}><span className="palantir-delivery-check"><Icon name="check"/></span><span><b>{item.title}</b><small>{item.campaign} · {new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${item.completedAt}T12:00:00.000Z`)).replace('.', '')}</small></span><em>{item.volume} vol.</em></div>)}</div> : <p className="palantir-panel-empty">Nenhuma conclusão registrada no período selecionado.</p>}</article>
          </div>
          <aside className="palantir-feedback">
            <div><span>FEEDBACKS</span><h3>Acompanhamento do colaborador</h3><p>Registre contexto, reconhecimento ou um próximo passo objetivo.</p></div>
            <form onSubmit={addCollaboratorFeedback}>
              <section className="feedback-evaluation" aria-labelledby="feedback-evaluation-title">
                <div className="feedback-evaluation-head"><span id="feedback-evaluation-title">AVALIAÇÃO</span><strong>{draftEvaluationScore === undefined ? 'Selecione os critérios' : <>{draftEvaluationScore.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}<small>/10</small></>}</strong></div>
                <fieldset><legend>Competência</legend><div className="feedback-rating-options">{collaboratorEvaluationOptions.competence.map(option => <button type="button" key={option.value} className={feedbackEvaluation.competence === option.value ? 'active' : ''} aria-pressed={feedbackEvaluation.competence === option.value} onClick={() => setFeedbackEvaluation(current => ({ ...current, competence: option.value }))}>{option.label}</button>)}</div></fieldset>
                <fieldset><legend>Produtividade</legend><div className="feedback-rating-options">{collaboratorEvaluationOptions.productivity.map(option => <button type="button" key={option.value} className={feedbackEvaluation.productivity === option.value ? 'active' : ''} aria-pressed={feedbackEvaluation.productivity === option.value} onClick={() => setFeedbackEvaluation(current => ({ ...current, productivity: option.value }))}>{option.label}</button>)}</div></fieldset>
                <fieldset><legend>Inteligência</legend><div className="feedback-rating-options">{collaboratorEvaluationOptions.intelligence.map(option => <button type="button" key={option.value} className={feedbackEvaluation.intelligence === option.value ? 'active' : ''} aria-pressed={feedbackEvaluation.intelligence === option.value} onClick={() => setFeedbackEvaluation(current => ({ ...current, intelligence: option.value }))}>{option.label}</button>)}</div></fieldset>
              </section>
              <textarea rows={5} maxLength={600} value={feedbackDraft} onChange={event => setFeedbackDraft(event.target.value)} placeholder={`Escreva um feedback para ${palantirAccount.name.split(' ')[0]}…`}/>
              <div className="feedback-form-actions"><small>{feedbackDraft.length}/600</small><button type="submit" disabled={!feedbackDraft.trim() || !completeFeedbackEvaluation}>Registrar feedback</button></div>
            </form>
            <div className="palantir-feedback-history"><h4>Histórico</h4>{palantirFeedbacks.length ? palantirFeedbacks.map(item => {
              const itemScore = item.evaluation ? collaboratorEvaluationScore(item.evaluation) : item.score
              return <article key={item.id}><div className="palantir-feedback-entry-head"><span>{typeof itemScore === 'number' ? `★ ${itemScore.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}/10` : 'Sem avaliação'}</span>{pendingFeedbackDeleteId === item.id ? <div className="feedback-delete-confirm"><span>Excluir?</span><button type="button" onClick={() => deleteCollaboratorFeedback(item.id)}>Sim</button><button type="button" onClick={() => setPendingFeedbackDeleteId(null)}>Não</button></div> : <button type="button" className="delete-feedback" onClick={() => setPendingFeedbackDeleteId(item.id)} aria-label={`Excluir feedback de ${item.authorName}`} title="Excluir feedback"><Icon name="trash"/></button>}</div>{item.evaluation && <div className="feedback-history-ratings"><span>{collaboratorEvaluationLabel('competence', item.evaluation.competence)}</span><span>{collaboratorEvaluationLabel('productivity', item.evaluation.productivity)}</span><span>{collaboratorEvaluationLabel('intelligence', item.evaluation.intelligence)}</span></div>}<p>{item.message}</p><div className="palantir-feedback-meta"><b>{item.authorName}</b><time>{feedbackDate(item.createdOn)}</time></div></article>
            }) : <div className="palantir-feedback-empty">Ainda não há feedbacks registrados para esta pessoa.</div>}</div>
          </aside>
        </div>
      </>}
    </section>}
  </div>
}
